import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import SVGSeparator from '../services/SVGSeparator';

import background from "../assets/img/chatgptfondplusieur.webp";       // hero
import backgroundImage from '../assets/img/chatgptmanger.webp';   // chapitre 01
import bouteillep from "../assets/img/fleurcogna.webp";           // chapitre 02
import img_plasir from "../assets/img/cogna.webp";                // chapitre 03
import img_innovation from "../assets/img/chatgptverre.webp";     // chapitre 04

/* ------------------------------------------------------------------ */
/* Petit hook "apparition au scroll" (IntersectionObserver).          */
/* Ajoute une classe quand l'élément entre dans le viewport.          */
/* ------------------------------------------------------------------ */
const useReveal = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, visible];
};

/* ------------------------------------------------------------------ */
/* Bloc "chapitre" : un numéro, un titre, du texte, une image.        */
/* reversed = image à gauche au lieu de droite.                       */
/* ------------------------------------------------------------------ */
const Chapter = ({ index, kicker, title, image, alt, children, reversed }) => {
  const [ref, visible] = useReveal();

  return (
    <section
      ref={ref}
      className="relative mx-auto max-w-7xl px-6 md:px-12 py-20 md:py-28"
    >
      <div
        className={`flex flex-col gap-12 lg:gap-20 items-center ${
          reversed ? 'lg:flex-row-reverse' : 'lg:flex-row'
        }`}
      >
        {/* Colonne texte */}
        <div
          className={`lg:w-1/2 transition-all duration-1000 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Numéro de chapitre */}
          <div className="flex items-center gap-4 mb-6">
            <span
              className="font-sans text-5xl md:text-6xl leading-none"
              style={{ color: '#E2C290' }}
            >
              {index}
            </span>
            <span className="h-px flex-1 max-w-[80px] bg-amber-700/40" />
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-amber-800">
              {kicker}
            </span>
          </div>

          <h2 className="font-sans text-3xl md:text-4xl lg:text-[2.75rem] leading-tight text-stone-800 mb-8">
            {title}
          </h2>

          <div className="space-y-4 font-nunito text-lg leading-relaxed text-stone-600">
            {children}
          </div>
        </div>

        {/* Colonne image */}
        <div
          className={`lg:w-1/2 w-full transition-all duration-1000 delay-150 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <div className="relative">
            {/* Cadre décalé en arrière-plan pour la profondeur */}
            <div
              className={`absolute -inset-3 rounded-2xl ${
                reversed ? 'rotate-[-1.5deg]' : 'rotate-[1.5deg]'
              }`}
              style={{ backgroundColor: '#F0E6D2' }}
              aria-hidden="true"
            />
            <img
              src={image}
              alt={alt}
              loading="lazy"
              decoding="async"
              className="relative w-full h-[320px] md:h-[460px] object-cover rounded-2xl shadow-xl"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* Séparateur de "partie" (Notre Histoire / Aujourd'hui).             */
/* ------------------------------------------------------------------ */
const PartTitle = ({ children }) => (
  <div className="text-center pt-16 pb-4">
    <h2 className="font-sans text-3xl md:text-5xl text-stone-800">{children}</h2>
    <SVGSeparator width={120} height={50} className="mx-auto" />
  </div>
);

const AboutUs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div style={{ backgroundColor: '#FBF7EF' }}>
      <Helmet>
        <title>À Propos | Au Coing du Ventoux</title>
        <meta name="description" content="Découvrez notre histoire et notre engagement dans la production artisanale d'alcool de coing." />
        <meta property="og:title" content="À Propos | Au Coing du Ventoux" />
        <meta property="og:url" content="https://aucoingduventoux.com/aboutus" />
        <link rel="canonical" href="https://aucoingduventoux.com/aboutus" />
      </Helmet>

      {/* ============================= HERO ============================= */}
      <header className="relative h-screen min-h-[600px] overflow-hidden">
        <img
          src={background}
          alt=""
          aria-hidden="true"
          fetchpriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Voile en dégradé + vignette centrale pour faire ressortir le texte
            même sur une image de fond claire et chargée. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(30,20,8,0.65) 0%, rgba(30,20,8,0.55) 45%, rgba(30,20,8,0.7) 100%)',
          }}
        />
        {/* Halo sombre concentré au centre, derrière le texte */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 60%)',
          }}
        />

        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6">
          <h1
            className="font-sans text-2xl md:text-3xl lg:text-4xl leading-tight max-w-3xl"
            style={{ textShadow: '0 2px 16px rgba(0,0,0,0.6)' }}
          >
            {t('aboutus.inspiration')}
          </h1>
          <SVGSeparator width={120} height={50} className="" />
          <p
            className="mt-4 font-nunito text-lg md:text-xl max-w-2xl text-stone-100/90"
            style={{ textShadow: '0 1px 10px rgba(0,0,0,0.6)' }}
          >
            {t('aboutus.inspiration_description')}
          </p>

          {/* Indicateur de scroll : positionnement sur le wrapper externe,
              animation sur l'élément interne (sinon animate-bounce écrase
              le -translate-x-1/2 et décale l'élément vers la droite). */}
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2">
            <div className="flex flex-col items-center gap-2 animate-bounce">
              <span className="text-[10px] tracking-[0.3em] uppercase text-white/70">
                Découvrir
              </span>
              <span className="block w-px h-8 bg-white/50" />
            </div>
          </div>
        </div>
      </header>

      {/* ====================== PARTIE 1 : HISTOIRE ===================== */}
      <PartTitle>{t('about.history.title')}</PartTitle>

      <Chapter
        index="01"
        kicker={t('about.history.subtitle')}
        title={t('about.history.description')}
        image={backgroundImage}
        alt="Produits du coing"
      >
        <p>{t('about.history.description1')}</p>
        <p>{t('about.history.description2')}</p>
        <p>{t('about.history.description3')}</p>
        <p>{t('about.history.description4')}</p>
      </Chapter>

      <Chapter
        index="02"
        kicker={t('about.history.production')}
        title={t('about.history.production_title')}
        image={bouteillep}
        alt="Production artisanale"
        reversed
      >
        <p>{t('about.history.production_description1')}</p>
        <p>{t('about.history.production_description2')}</p>
        <p>{t('about.history.production_description3')}</p>
        <p>{t('about.history.production_description4')}</p>
      </Chapter>

      {/* ===================== PARTIE 2 : AUJOURD'HUI =================== */}
      <PartTitle>{t('about.today.title')}</PartTitle>

      <Chapter
        index="03"
        kicker={t('about.today.title_orange')}
        title={t('about.today.title_part_1')}
        image={img_plasir}
        alt="Le coing aujourd'hui"
      >
        <p>{t('about.today.description1')}</p>
        <p>{t('about.today.description2')}</p>
      </Chapter>

      <Chapter
        index="04"
        kicker={t('about.innovation.title')}
        title={t('about.today.title_part_2')}
        image={img_innovation}
        alt="Innovation"
        reversed
      >
        <p>{t('about.today.innovation_description1')}</p>
        <p>{t('about.today.innovation_description2')}</p>
        <p>{t('about.today.innovation_description3')}</p>
        <p>{t('about.today.innovation_description4')}</p>
        <p>{t('about.today.innovation_description5')}</p>
        <p>{t('about.today.innovation_description6')}</p>
        <p>{t('about.today.innovation_description7')}</p>
      </Chapter>

      {/* ========================= CTA FINAL =========================== */}
      <section className="text-center px-6 pb-28 pt-8">
        <button
          onClick={() => navigate('/boutique')}
          className="font-sans inline-block px-10 py-4 rounded-full text-white text-lg transition-transform duration-300 hover:scale-105 shadow-lg"
          style={{ backgroundColor: '#1c1917' }}
        >
          Découvrir la boutique
        </button>
      </section>
    </div>
  );
};

export default AboutUs;